<script setup lang="ts">
/**
 * Trending topics, from the hashtags people actually typed.
 *
 * This panel shipped empty for a long time with a note saying why: nothing parsed a
 * hashtag, nothing indexed one, and nothing counted them, so every number in the design
 * would have been invented. `post_hashtags` and the tag index are what filled it.
 *
 * Two things it still does not do, and they are the same two reasons:
 *
 *  - **No category per topic.** The design printed "Trending in Thailand" above each
 *    one. Nothing here says where a tag is popular — there is no geography on a post
 *    beyond the author's own country, and a country over a tag would be a claim no
 *    query produced. The window is stated once instead, which is a fact.
 *  - **No settings.** The gear is still inert: there is one ranking, it is documented,
 *    and a control that offers to change it would be offering nothing.
 *
 * The count is public because the posts behind it are: `PUBLIC_POST_PREDICATE` on the
 * server keeps private groups, hidden walls and unlisted pages out of both the list and
 * the number — a count that included them would publish the existence of what no tag
 * page would show.
 */
import { onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { HASHTAG_LIMITS, type TrendingTag } from '@talkvibe/shared';

import NavIcon from './NavIcon.vue';
import { pageService } from '../services/pages.ts';
import { useI18n } from '../i18n/index.ts';

const { t, formatNumber } = useI18n();

const topics = ref<TrendingTag[]>([]);

/**
 * The tags used most in the last `trendingWindowDays`.
 *
 * Counted over public posts only, which is what lets the number be shown at all: a
 * count that included private posts would publish the existence of what no tag page
 * would show.
 *
 * A failure leaves the panel empty rather than announcing itself — it is a garnish on
 * the column, and the empty state already explains itself.
 */
async function load(): Promise<void> {
  try {
    topics.value = (await pageService.trendingTags()).items;
  } catch {
    topics.value = [];
  }
}

onMounted(() => void load());
</script>

<template>
  <section class="rounded-xl bg-surface-container-lowest p-space-md shadow-sm">
    <div class="mb-3 flex items-center justify-between gap-2">
      <h2 class="min-w-0 text-headline-sm text-on-surface">{{ t('trending.title') }}</h2>
      <button
        type="button"
        disabled
        class="shrink-0 cursor-not-allowed text-on-surface-variant opacity-60"
        :aria-label="t('trending.settings')"
        :title="t('common.notReadyYet')"
      >
        <NavIcon name="settings" class="h-5 w-5" />
      </button>
    </div>

    <template v-if="topics.length">
      <!--
        The design printed "Trending in Thailand" above each topic. Nothing here
        categorises a tag, so the window is stated once instead — a fact, rather than a
        country no query produced.
      -->
      <p class="pb-2 text-label-sm text-outline">
        {{ t('trending.window', { days: String(HASHTAG_LIMITS.trendingWindowDays) }) }}
      </p>
      <ol class="space-y-3">
        <li v-for="(entry, index) in topics" :key="entry.tag">
          <RouterLink
            :to="`/tags/${encodeURIComponent(entry.tag)}`"
            class="group -mx-2 block rounded-lg px-2 py-2 transition-colors hover:bg-surface-container-low"
          >
            <span class="flex items-center justify-between text-label-sm text-on-surface-variant">
              <span class="tabular-nums">{{ index + 1 }}</span>
              <NavIcon name="insights" class="h-4 w-4" />
            </span>
            <span
              class="mt-0.5 block truncate text-headline-sm text-on-surface transition-colors group-hover:text-primary"
            >
              #{{ entry.tag }}
            </span>
            <span class="text-label-sm tabular-nums text-outline">
              {{ t('trending.posts', { count: formatNumber(entry.postCount) }) }}
            </span>
          </RouterLink>
        </li>
      </ol>
    </template>

    <!--
      Empty because nobody has used a hashtag in the window, not because
      nothing is collected.
      This block used to say the opposite and carry a "soon" badge, which was
      true before `post_hashtags` existed and has been a lie since. An empty
      state that blames an unbuilt feature stops anybody reporting that the
      built one is broken.
    -->
    <div v-else class="flex items-start gap-2.5 rounded-lg bg-surface-container-low px-3 py-2.5">
      <NavIcon name="insights" class="mt-0.5 h-5 w-5 shrink-0 text-outline" />
      <div class="min-w-0 flex-1">
        <p class="text-label-md text-on-surface">{{ t('trending.empty') }}</p>
        <p class="mt-0.5 text-body-sm leading-snug text-on-surface-variant">
          {{
            t('trending.emptyBody', { days: String(HASHTAG_LIMITS.trendingWindowDays) })
          }}
        </p>
      </div>
    </div>
  </section>
</template>
